import type { Property, PropertyOption, TimeEntry } from "./types";

export function cn(...classes: (string | false | null | undefined)[]) {
  return classes.filter(Boolean).join(" ");
}

const dateFmt = new Intl.DateTimeFormat(undefined, { month: "long", day: "numeric", year: "numeric" });
const shortDateFmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const dateTimeFmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

/** Parses YYYY-MM-DD as a local date (not UTC). */
export function parseDay(day: string) {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function formatDay(day: string | null | undefined, short = false) {
  if (!day) return "";
  const date = parseDay(day);
  if (short) {
    return date.getFullYear() === new Date().getFullYear() ? shortDateFmt.format(date) : dateFmt.format(date);
  }
  return dateFmt.format(date);
}

export function formatDateTime(iso: string | null | undefined) {
  return iso ? dateTimeFmt.format(new Date(iso)) : "";
}

export function timeAgo(iso: string) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 45) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.round(diff / 86400)}d ago`;
  return formatDateTime(iso);
}

export function formatDuration(totalSeconds: number, { seconds = false } = {}) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const parts: string[] = [];
  if (d) parts.push(`${d}d`);
  if (h || d) parts.push(`${h}h`);
  parts.push(`${m}m`);
  if (seconds && !d) parts.push(`${sec}s`);
  return parts.join(" ");
}

export function entrySeconds(e: TimeEntry, now = Date.now()) {
  return e.closedSeconds + (e.openSince ? (now - new Date(e.openSince).getTime()) / 1000 : 0);
}

export function totalSeconds(entries: TimeEntry[], now = Date.now()) {
  return entries.reduce((sum, e) => sum + entrySeconds(e, now), 0);
}

export function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function optionMap(properties: Property[]) {
  const map = new Map<string, PropertyOption>();
  for (const p of properties) for (const o of p.options) map.set(o.id, o);
  return map;
}

const avatarColors = ["#e16259", "#d9730d", "#dfab01", "#0f7b6c", "#0b6e99", "#6940a5", "#ad1a72", "#4d6461"];

export function avatarColor(id: string) {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

export function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] ?? "?").toUpperCase();
}

export function taskKey(projectKey: string | undefined, number: number) {
  return projectKey ? `${projectKey}-${number}` : `#${number}`;
}

/** Midpoint position for inserting between two neighbours in a manually ordered list. */
export function positionBetween(before: number | undefined, after: number | undefined) {
  if (before === undefined && after === undefined) return 1;
  if (before === undefined) return (after as number) - 1;
  if (after === undefined) return before + 1;
  return (before + after) / 2;
}

export function pluralize(n: number, word: string, plural = `${word}s`) {
  return `${n} ${n === 1 ? word : plural}`;
}
